// Identity / member query layer. Pairs with `lib/views/identity.ts` (server
// fetchers used during SSR prefetch) and `lib/mutations/identity.ts`.
// No 'use client' directive: the hooks are React hooks but they're called
// from client components. Server code (pages, layouts) imports the query
// leaves to set up prefetches.
//
// Per the react-query-options-migration design: every read is a `queryOptions`
// / `infiniteQueryOptions` factory whose cache key and fetch input derive from
// one normalized `args` object built adjacent to the `queryFn`. Inner-node
// prefixes (`all`, `members`) stay bare arrays for invalidation; leaves return
// `queryOptions`. Normalization happens once, in the leaf.

import {
  queryOptions,
  infiniteQueryOptions,
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { asUserId, asSessionId } from '@leanin/shared/ids';
import type { UserId } from '@leanin/shared/ids';
import type { AuthedSession } from '@leanin/shared/auth/session';
import type { AuthDriftReason } from '@leanin/shared/auth/drift';
import { SELF_REACTIVATE_WINDOW_MS } from '@leanin/shared/auth/windows';
import type { PlatformRole } from '@leanin/shared/views/profile';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import { sortedStrings } from './normalize';

export type MyProfileView = components['schemas']['ProfileSelfResponse'];
export type MemberView = components['schemas']['ProfileView'];
export type ProfileView = components['schemas']['ProfileView'];
export type UserMutualsView = components['schemas']['MemberMutuals'];
export type MyPrivacyView = components['schemas']['UserPrivacyView'];
export type MembersCountByScopeView =
  components['schemas']['DirectoryScopeCounts'];

export type ListMembersInput = {
  q?: string;
  sort?: 'recent' | 'name' | 'connection' | 'recommended';
  cursor?: string;
  pageSize?: number;
  placeCountryCode?: string;
  networkId?: string;
  scope?: 'circle' | 'network';
  /** Multi-value filter for the unified place_text column. The server accepts
   *  an array so callers can OR-filter across several cities. */
  placeText?: readonly string[];
  networks?: readonly string[];
  industries?: readonly string[];
  careerStages?: readonly string[];
  openTo?: readonly string[];
};

// The normalized member-list args: array filters sorted so the same set in a
// different order produces the same cache key (closes order-dependent drift).
// `cursor` is fetch-only — injected via `pageParam` for the infinite leaf and
// never part of the key.
type NormalizedListMembersArgs = {
  q?: string;
  sort?: ListMembersInput['sort'];
  pageSize?: number;
  placeCountryCode?: string;
  networkId?: string;
  scope?: ListMembersInput['scope'];
  placeText?: readonly string[];
  networks?: readonly string[];
  industries?: readonly string[];
  careerStages?: readonly string[];
  openTo?: readonly string[];
};

function normalizeListMembers(
  input: Omit<ListMembersInput, 'cursor'>,
): NormalizedListMembersArgs {
  const args: NormalizedListMembersArgs = {};
  if (input.q !== undefined) args.q = input.q;
  if (input.sort !== undefined) args.sort = input.sort;
  if (input.pageSize !== undefined) args.pageSize = input.pageSize;
  if (input.placeCountryCode !== undefined)
    args.placeCountryCode = input.placeCountryCode;
  if (input.networkId !== undefined) args.networkId = input.networkId;
  if (input.scope !== undefined) args.scope = input.scope;
  if (input.placeText !== undefined) args.placeText = sortedStrings(input.placeText);
  if (input.networks !== undefined) args.networks = sortedStrings(input.networks);
  if (input.industries !== undefined)
    args.industries = sortedStrings(input.industries);
  if (input.careerStages !== undefined)
    args.careerStages = sortedStrings(input.careerStages);
  if (input.openTo !== undefined) args.openTo = sortedStrings(input.openTo);
  return args;
}

// ---- Query keys + leaves ----

export const identityKeys = {
  all: ['identity'] as const,
  members: () => [...identityKeys.all, 'members'] as const,

  me: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'me'] as const,
      queryFn: fetchMyProfile,
      // me changes rarely; keep it fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
    }),

  member: (id: UserId) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'member', id] as const,
      queryFn: () => fetchMember(id),
    }),

  memberMutuals: (id: UserId) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'member', id, 'mutuals'] as const,
      queryFn: () => fetchMemberMutuals(id),
    }),

  // Single-page member list (e.g. mention popover). Normalize once; `args` is
  // the terminal key element and the sole fetch input.
  membersList: (raw: ListMembersInput) => {
    const args = normalizeListMembers(raw);
    return queryOptions({
      queryKey: [...identityKeys.members(), args] as const,
      queryFn: () => fetchMembers(args),
    });
  },

  // Cursor-paginated member list (directory). Same normalized `args` in the
  // key; cursor injected via `pageParam`, never in the key.
  membersInfinite: (raw: Omit<ListMembersInput, 'cursor'>) => {
    const args = normalizeListMembers(raw);
    return infiniteQueryOptions({
      queryKey: [...identityKeys.members(), args] as const,
      queryFn: ({ pageParam }) =>
        fetchMembers({ ...args, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        (lastPage as { nextCursor?: string | null }).nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    });
  },

  membersCountByScope: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'members', 'count-by-scope'] as const,
      queryFn: fetchMembersCountByScope,
    }),

  membersOnline: (ids: readonly UserId[]) => {
    const sortedIds = [...ids].sort();
    return queryOptions({
      queryKey: [...identityKeys.all, 'members', 'online', sortedIds] as const,
      queryFn: () => fetchOnlineMembers(sortedIds),
      enabled: ids.length > 0,
      // online status changes frequently — refresh on a short cadence
      staleTime: 30 * 1000,
    });
  },

  memberProfiles: (ids: readonly UserId[]) => {
    const sortedIds = [...ids].sort();
    return queryOptions({
      queryKey: [...identityKeys.all, 'members', 'profiles', sortedIds] as const,
      queryFn: () => fetchMemberProfiles(sortedIds),
      enabled: ids.length > 0,
    });
  },

  follow: (id: UserId) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'follow', id] as const,
      queryFn: () => fetchIsFollowing(id),
    }),

  privacy: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'privacy'] as const,
      queryFn: fetchMyPrivacy,
    }),

  consentHistory: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'consent-history'] as const,
      queryFn: fetchMyConsentHistory,
    }),

  notificationPreferences: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'notifications', 'preferences'] as const,
      queryFn: fetchMyNotificationPreferences,
    }),

  sessions: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'sessions'] as const,
      queryFn: fetchMySessions,
    }),

  connectedAccounts: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'connected-accounts'] as const,
      queryFn: fetchMyConnectedAccounts,
    }),
};

// ---- Client fetchers ----

export async function fetchMyProfile(): Promise<MyProfileView> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/members/me');
  if (data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchMember(id: UserId): Promise<MemberView> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/members/{id}', {
    params: { path: { id } },
  });
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchIsFollowing(id: UserId): Promise<boolean> {
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/me/follows/{userId}', {
    params: { path: { userId: id } },
  });
  return data?.following ?? false;
}

export async function fetchMemberMutuals(
  id: UserId,
): Promise<UserMutualsView> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/members/{userId}/mutuals',
    { params: { path: { userId: id } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchMembers(input: NormalizedListMembersArgs & { cursor?: string }) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/members', {
    params: {
      query: {
        q: input.q,
        sort: input.sort,
        cursor: input.cursor,
        pageSize: input.pageSize,
        placeCountryCode: input.placeCountryCode,
        networkId: input.networkId,
        scope: input.scope,
        ...(input.placeText !== undefined
          ? { placeText: input.placeText as string[] }
          : {}),
        ...(input.networks !== undefined
          ? { networks: input.networks as unknown as string[] }
          : {}),
        ...(input.industries !== undefined
          ? { industries: input.industries as string[] }
          : {}),
        ...(input.careerStages !== undefined
          ? { careerStages: input.careerStages as string[] }
          : {}),
        ...(input.openTo !== undefined
          ? { openTo: input.openTo as string[] }
          : {}),
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchOnlineMembers(
  ids: readonly UserId[],
): Promise<Set<UserId>> {
  if (ids.length === 0) return new Set();
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/members/online', {
    params: { query: { userIds: ids as unknown as string[] } },
  });
  if (data === undefined) return new Set();
  return new Set(data.onlineUserIds as UserId[]);
}

export async function fetchMyPrivacy(): Promise<MyPrivacyView> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/members/me/privacy');
  if (data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchMyConsentHistory() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/members/me/consent-history',
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchMyNotificationPreferences() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/members/me/notification-preferences',
  );
  if (data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchMySessions() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/members/me/sessions');
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchMyConnectedAccounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/members/me/connected-accounts',
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchMembersCountByScope(): Promise<MembersCountByScopeView> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/members/count-by-scope',
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchMemberProfiles(
  ids: readonly UserId[],
): Promise<Map<UserId, ProfileView>> {
  if (ids.length === 0) return new Map();
  const client = getApiClient();
  const { data, error } = await client.POST('/api/v1/members/profiles', {
    body: { userIds: ids as unknown as string[] },
  });
  if (data === undefined) throwApiError(error);
  return new Map(data.profiles.map((p) => [p.id as UserId, p]));
}

// ---- Session query ----
//
// The canonical session leaf. One cache key (`['me', 'context']`) — every
// consumer (router guards, layouts, component hooks) routes through the same
// TanStack subscription, so a single network call backs the session window.
// Co-located here per the migration design so route loaders (which bypass
// hooks) and `use-session.ts`'s hooks share one definition.

export type Member = {
  id: string;
  firstName: string;
  lastName: string;
  bio: string | null;
  photoStorageKey: string | null;
  photoStatus: string;
  platformRole: PlatformRole | null;
  emailVerifiedAt: Date | null;
  onboardingCompletedAt: Date | null;
  restricted: boolean;
  deactivatedAt: Date | null;
};

// Binary context. The server's auth middleware Just-In-Time provisions the
// local member row on first authed request for a new Clerk user (see
// `apps/server/src/identity/service/load-session.ts`), so the SPA never sees
// an "orphan" state — either we have an authed session or we don't.
export type MyContext = { session: AuthedSession; member: Member } | null;

function isUsable(m: Member): boolean {
  if (m.restricted) return false;
  if (m.deactivatedAt !== null) {
    const elapsed = Date.now() - m.deactivatedAt.getTime();
    if (elapsed > SELF_REACTIVATE_WINDOW_MS) return false;
  }
  return true;
}

// Clerk attaches itself to `window.Clerk` once the SDK initialises (see
// ClerkProvider in `main.tsx`). Typed loosely here — we only need `signOut`
// + a session-presence probe, and importing the SDK type pulls in heavy types
// we don't otherwise need at the api-client layer.
type ClerkLike = {
  readonly session?: unknown;
  signOut: () => Promise<void>;
};

function parseAuthState(error: unknown): AuthDriftReason | null {
  const inner = (error as { error?: { authState?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  const v = inner.authState;
  return v === 'restricted' || v === 'deactivated_expired' ? v : null;
}

// Reconcile when the SDK and the server disagree on a drift-final state.
// Drift-final = the server positively identified that the local member row is
// non-recoverable (restricted or hard-deactivated past the self-reactivate
// window) AND the caller had a valid Clerk JWT. Dropping the SDK session here
// is the right move because the Clerk session can no longer back a usable app
// session. For every OTHER 401 (transient backend failure, Clerk outage,
// plain anonymous, unknown) we leave the SDK alone — server-side flakiness
// should not destructively sign the user out on first occurrence.
function reconcileStaleClerkSession(): void {
  if (typeof window === 'undefined') return;
  const clerk = (window as unknown as { Clerk?: ClerkLike }).Clerk;
  if (!clerk?.session) return;
  document.cookie = 'dev_user_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  void clerk.signOut();
}

async function fetchMyContext(): Promise<MyContext> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/me/context');
  if (!data) {
    if (response.status === 401) {
      // Per Option B of the recovery-design: discriminate on the
      // server-emitted `authState`. Reconcile (destructively drop the Clerk
      // SDK session) ONLY when the server has positively identified a
      // drift-final cause. Bare UNAUTHENTICATED — the transient case —
      // returns null without reconciling, so the next poll picks the session
      // back up cleanly when the upstream recovers. (Today's failure mode: a
      // single Clerk Backend API outage or DB blip otherwise destructively
      // logged every signed-in user out across the deployment.)
      const authState = parseAuthState(error);
      if (authState !== null) reconcileStaleClerkSession();
      return null;
    }
    if (response.status === 404) return null;
    throwApiError(error);
  }
  const raw = data.member;
  const member: Member = {
    id: raw.id,
    firstName: raw.firstName,
    lastName: raw.lastName,
    bio: raw.bio ?? null,
    photoStorageKey: raw.photoStorageKey ?? null,
    photoStatus: raw.photoStatus,
    platformRole: raw.platformRole ?? null,
    emailVerifiedAt: raw.emailVerifiedAt != null ? new Date(raw.emailVerifiedAt) : null,
    onboardingCompletedAt:
      raw.onboardingCompletedAt != null ? new Date(raw.onboardingCompletedAt) : null,
    restricted: raw.restricted,
    deactivatedAt: raw.deactivatedAt != null ? new Date(raw.deactivatedAt) : null,
  };
  if (!isUsable(member)) return null;
  const s = data.session;
  const session: AuthedSession = {
    sessionId: asSessionId(s.sessionId),
    userId: asUserId(s.userId),
    platformRole: s.platformRole ?? null,
    emailVerified: s.emailVerified,
    stepUpValidUntil: s.stepUpValidUntil != null ? new Date(s.stepUpValidUntil) : null,
    absoluteExpiresAt: new Date(s.absoluteExpiresAt),
    idleExpiresAt: new Date(s.idleExpiresAt),
  };
  return { session, member };
}

export const sessionQuery = queryOptions({
  queryKey: ['me', 'context'] as const,
  queryFn: fetchMyContext,
  staleTime: 60_000,
});

// Inverse of the `_authed` guard: if the visitor already has a usable session,
// bounce them to /feed. Used by `/auth/login`, `/auth/signup`, and
// `/auth/password-reset` — those pages don't make sense for signed-in users
// (and Clerk rejects `signIn.create()` calls while signed in).
//
// Mirrors `_authed.tsx`'s try/redirect split: redirect() returns a `Response`
// instance, so we isolate the fetch and throw outside the try.
export async function redirectIfSignedIn(context: {
  queryClient: QueryClient;
}): Promise<void> {
  let ctx: MyContext;
  try {
    ctx = await context.queryClient.ensureQueryData(sessionQuery);
  } catch {
    return;
  }
  if (ctx !== null) throw redirect({ to: '/feed' });
}

// Onboarding gate. A signed-in visitor whose `onboardingCompletedAt` is null
// hasn't finished the persona tour — they get bounced to `/onboarding/profile`.
// Anonymous visitors are passed through (the gate only fires when `ctx` is
// non-null).
export function gateOnboardingComplete(ctx: MyContext): void {
  if (ctx === null) return;
  if (ctx.member.onboardingCompletedAt === null) {
    throw redirect({ to: '/onboarding/profile' });
  }
}

// Inverse of `gateOnboardingComplete`, applied by the `/onboarding` layout: a
// signed-in visitor who has ALREADY finished onboarding has no business on the
// `/onboarding/*` flow — bounce them to /feed.
export function gateOnboardingIncomplete(ctx: MyContext): void {
  if (ctx === null) return;
  if (ctx.member.onboardingCompletedAt !== null) {
    throw redirect({ to: '/feed' });
  }
}

// ---- Hooks ----

export function useMe() {
  return useQuery(identityKeys.me());
}

export function useMember(id: UserId) {
  return useQuery(identityKeys.member(id));
}

export function useIsFollowing(id: UserId) {
  return useQuery(identityKeys.follow(id));
}

export function useMemberMutuals(id: UserId) {
  return useQuery(identityKeys.memberMutuals(id));
}

export function useMembers(input: ListMembersInput) {
  return useQuery(identityKeys.membersList(input));
}

export function useInfiniteMembers(input: Omit<ListMembersInput, 'cursor'>) {
  return useInfiniteQuery(identityKeys.membersInfinite(input));
}

export function useOnlineMembers(ids: readonly UserId[]) {
  return useQuery(identityKeys.membersOnline(ids));
}

export function useMemberProfiles(ids: readonly UserId[]) {
  return useQuery(identityKeys.memberProfiles(ids));
}

export function useMyPrivacy() {
  return useQuery(identityKeys.privacy());
}

export function useMembersCountByScope() {
  return useQuery(identityKeys.membersCountByScope());
}
